import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * The counters a nav row can carry, published by the page that already loaded
 * the rows rather than fetched by the shell — or, for „Aktivitäten", by the
 * shell's own count read (IMM-1360.4). A shell-wide fetch of rows on every
 * navigation to paint one number is the wrong trade; the page holds the answer
 * already, so it hands it up. A pair of indexed COUNTs is the exception the
 * ticket asks for (D2).
 *
 * A key with no entry — or an entry of `0` — renders nothing: „no Bewertungen"
 * is the empty state's job to say, and a nav counter reading „0" says it a
 * second time, worse.
 */
export type NavCounts = Readonly<Record<string, number | undefined>>;

type NavCountsValue = {
  readonly counts: NavCounts;
  readonly publish: (key: string, count: number | undefined) => void;
  readonly refreshTicks: Readonly<Record<string, number>>;
  readonly requestRefresh: (key: string) => void;
};

// Without a provider the context is an inert no-op rather than a throw: a page
// must not break because the shell it happens to be mounted without is missing,
// and component tests mount the page on its own.
const NavCountsContext = createContext<NavCountsValue>({
  counts: {},
  publish: () => {},
  refreshTicks: {},
  requestRefresh: () => {},
});

export function NavCountsProvider({ children }: { readonly children: ReactNode }) {
  const [counts, setCounts] = useState<NavCounts>({});
  const [refreshTicks, setRefreshTicks] = useState<Readonly<Record<string, number>>>({});

  const publish = useCallback((key: string, count: number | undefined) => {
    setCounts((current) => (current[key] === count ? current : { ...current, [key]: count }));
  }, []);

  const requestRefresh = useCallback((key: string) => {
    setRefreshTicks((current) => ({ ...current, [key]: (current[key] ?? 0) + 1 }));
  }, []);

  const value = useMemo<NavCountsValue>(
    () => ({ counts, publish, refreshTicks, requestRefresh }),
    [counts, publish, refreshTicks, requestRefresh],
  );

  return <NavCountsContext.Provider value={value}>{children}</NavCountsContext.Provider>;
}

export function useNavCounts(): NavCounts {
  return useContext(NavCountsContext).counts;
}

/** Ask the counter's owner to read `key` again — for a page that changed what it counts without owning the number. */
export function useRequestNavCountRefresh(): (key: string) => void {
  return useContext(NavCountsContext).requestRefresh;
}

/** Bumps each time `useRequestNavCountRefresh()(key)` is called; an effect dependency for the counter's owner. */
export function useNavCountRefreshTick(key: string): number {
  return useContext(NavCountsContext).refreshTicks[key] ?? 0;
}

/**
 * Publish `count` under `key` for as long as the calling component is mounted,
 * clearing it on unmount so a stale number never outlives the page that knew it.
 */
export function usePublishNavCount(key: string, count: number | undefined): void {
  const { publish } = useContext(NavCountsContext);

  useEffect(() => {
    publish(key, count);

    return () => publish(key, undefined);
  }, [publish, key, count]);
}
